import type { Middleware } from "openapi-fetch";
import type { components } from "api/generated/tenant-api";

type PermissionDeniedErrorResponse = components["schemas"]["PermissionDeniedErrorResponse"];

export type RequiredPermission = components["schemas"]["PermissionDeniedRequiredPermission"];

// ロールの権限が足りず、API を実行できなかったこと。要る権限は、複数あればどれか 1 つで実行できる
export type PermissionDenial = {
  requiredPermissions: RequiredPermission[];
  requiresRoot: boolean;
};

const isPermissionDeniedBody = (body: unknown): body is PermissionDeniedErrorResponse =>
  typeof body === "object" &&
  body !== null &&
  (body as { error?: unknown }).error === "PERMISSION_DENIED" &&
  Array.isArray((body as { requiredPermissions?: unknown }).requiredPermissions);

// tenant-backend-v2 の 403 のうち、ロールの権限が足りなかったもの（GlobalExceptionHandler#handlePermissionDenied）だけを拾う。
// 機能ごとの判定が返す 403（本人でない・閲覧範囲の外など）は形が違うので拾わない
export const parsePermissionDenial = (status: number, body: unknown): PermissionDenial | null => {
  if (status !== 403 || !isPermissionDeniedBody(body)) return null;
  return { requiredPermissions: body.requiredPermissions, requiresRoot: body.requiresRoot === true };
};

// 権限不足の通知の登録先。API の middleware と画面の通知の両方から使うので React の外に置く
export const createPermissionDeniedStore = () => {
  const listeners = new Set<(denial: PermissionDenial) => void>();
  return {
    emit(denial: PermissionDenial) {
      listeners.forEach((listener) => listener(denial));
    },
    subscribe(listener: (denial: PermissionDenial) => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type PermissionDeniedStore = ReturnType<typeof createPermissionDeniedStore>;

export const permissionDeniedStore = createPermissionDeniedStore();

// 権限不足の 403 なら通知へ伝える。tenantApiClient を通さずに fetch で v2 を呼ぶ箇所（CSV のダウンロードなど）からも使う。
// response の本文は読まずに残す（clone を読む）
export const notifyPermissionDenial = async (response: Response, store: PermissionDeniedStore = permissionDeniedStore) => {
  if (response.status !== 403) return;
  try {
    const denial = parsePermissionDenial(response.status, await response.clone().json());
    if (denial) store.emit(denial);
  } catch {
    // 本文が JSON でない 403 は権限不足の形ではないので、何もしない
  }
};

// REST（tenant-backend-v2）の権限不足の 403 を通知へ伝える。画面ごとのエラー処理は変えず、response もそのまま返す
export const permissionDeniedFetchMiddleware = (store: PermissionDeniedStore): Middleware => ({
  async onResponse({ response }) {
    await notifyPermissionDenial(response, store);
    return response;
  },
});
